// Service Type Utilities
// Handles service type lookups, validation, and business logic

import { supabase } from './supabaseClient';

// Cache service types to avoid repeated database calls
let serviceTypesCache = null;
let cacheTimestamp = null;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

/**
 * Fetch all active service types from the database
 * Uses caching to minimize database calls
 */
export const fetchServiceTypes = async (forceRefresh = false) => {
  try {
    const now = Date.now();
    
    // Return cached data if available and not expired
    if (!forceRefresh && serviceTypesCache && cacheTimestamp && 
        (now - cacheTimestamp) < CACHE_DURATION) {
      return { data: serviceTypesCache, error: null };
    }

    const { data, error } = await supabase
      .from('service_types')
      .select('*')
      .eq('is_active', true)
      .order('name');

    if (error) {
      // If service_types table doesn't exist, return default service type
      if (error.code === '42P01' || error.message.includes('relation "public.service_types" does not exist')) {
        console.warn('Service types table not found, using defaults');
        const defaultTypes = [getDefaultServiceType()];
        serviceTypesCache = defaultTypes;
        cacheTimestamp = now;
        return { data: defaultTypes, error: null };
      }
      throw error;
    }

    if (!error && data) {
      serviceTypesCache = data;
      cacheTimestamp = now;
    }

    return { data, error };
  } catch (error) {
    console.error('Error fetching service types:', error);
    // Return default service type on any error
    const defaultTypes = [getDefaultServiceType()];
    return { data: defaultTypes, error: null };
  }
};

/**
 * Get a specific service type by name
 * Returns the default service type if not found
 */
export const getServiceType = async (serviceTypeName) => {
  try {
    const { data: serviceTypes, error } = await fetchServiceTypes();
    
    if (error) {
      console.error('Error fetching service types:', error);
      return getDefaultServiceType();
    }

    // Find by exact name match (case-insensitive)
    const serviceType = serviceTypes.find(st => 
      st.name.toLowerCase() === (serviceTypeName || '').toLowerCase()
    );

    // Return found service type or default
    return serviceType || getDefaultServiceType();
  } catch (error) {
    console.error('Error getting service type:', error);
    return getDefaultServiceType();
  }
};

/**
 * Get the default service type (Initial Service Call)
 * This is the fallback for all legacy data and unknown service types
 */
export const getDefaultServiceType = () => ({
  id: 'default',
  name: 'Initial Service Call',
  code: 'INITIAL',
  rate_type: 'FLAT',
  flat_completion_rate: 300,
  flat_refusal_rate: 50,
  hourly_rate: null,
  is_default: true,
  is_active: true,
  description: 'Standard Coca-Cola service - $300 per completion, $50 per refusal'
});

/**
 * Calculate billable hours from timestamps
 * Returns exact hours without rounding
 */
export const calculateBillableHours = (timestamps) => {
  try {
    const {
      travel_time_start,
      travel_time_end,
      arrival_time,
      departure_time
    } = timestamps;
    // Helper: convert Excel serials or ISO strings to Date
    const toDate = (val) => {
      if (val === undefined || val === null) return null;
      // Numeric or numeric-like string => Excel serial days
      const num = typeof val === 'number' ? val : (typeof val === 'string' && /^\d+(\.\d+)?$/.test(val.trim()) ? Number(val) : NaN);
      if (!isNaN(num)) {
        const ms = (num - 25569) * 86400 * 1000; // Excel serial days to ms since Unix epoch
        return new Date(ms);
      }
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    };

    let totalHours = 0;

    // Calculate travel hours
    if (travel_time_start && travel_time_end) {
      const travelStart = toDate(travel_time_start);
      const travelEnd = toDate(travel_time_end);
      const travelHours = (travelEnd - travelStart) / (1000 * 60 * 60);
      
      if (travelHours > 0 && travelHours < 24) { // Sanity check
        totalHours += travelHours;
      }
    }

    // Calculate onsite hours
    if (arrival_time && departure_time) {
      const arrival = toDate(arrival_time);
      const departure = toDate(departure_time);
      const onsiteHours = (departure - arrival) / (1000 * 60 * 60);
      
      if (onsiteHours > 0 && onsiteHours < 24) { // Sanity check
        totalHours += onsiteHours;
      }
    }

    // Return exact hours without rounding
    return totalHours;
  } catch (error) {
    console.error('Error calculating billable hours:', error);
    return 0;
  }
};

/**
 * Parse hours from various timestamp formats
 * Handles different date/time formats gracefully
 */
export const parseTimestamp = (timestampString) => {
  if (!timestampString) return null;

  try {
    // Handle various formats
    const timestamp = new Date(timestampString);
    
    // Check if valid date
    if (isNaN(timestamp.getTime())) {
      // Try parsing MM/DD/YYYY HH:MM format
      const parts = timestampString.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})\s+(\d{1,2}):(\d{2})/);
      if (parts) {
        const [_, month, day, year, hour, minute] = parts;
        return new Date(year, month - 1, day, hour, minute);
      }
      
      return null;
    }
    
    return timestamp;
  } catch (error) {
    console.error('Error parsing timestamp:', timestampString, error);
    return null;
  }
};

/**
 * Calculate a detailed hours breakdown: travel, onsite, and total hours.
 * - Uses explicit onsite_minutes if provided; otherwise computes from arrival/departure
 * - Computes travel hours from travel start/end if present
 */
export const calculateHoursBreakdown = (timestamps) => {
  try {
    const {
      travel_time_start,
      travel_time_end,
      arrival_time,
      departure_time,
      onsite_minutes,
      travel_minutes
    } = timestamps || {};

    // Helper: convert Excel serials or ISO strings to Date
    const toDate = (val) => {
      if (val === undefined || val === null) return null;
      const num = typeof val === 'number' ? val : (typeof val === 'string' && /^\d+(\.\d+)?$/.test(val.trim()) ? Number(val) : NaN);
      if (!isNaN(num)) {
        const ms = (num - 25569) * 86400 * 1000;
        return new Date(ms);
      }
      const d = new Date(val);
      return isNaN(d.getTime()) ? null : d;
    };

    let travelHours = 0;
    let onsiteHours = 0;

    // Travel hours: prefer start/end timestamps, fallback to explicit minutes
    if (travel_time_start && travel_time_end) {
      const travelStart = toDate(travel_time_start);
      const travelEnd = toDate(travel_time_end);
      const raw = (travelEnd - travelStart) / (1000 * 60 * 60);
      if (!isNaN(raw) && raw > 0 && raw < 24) travelHours = raw;
    } else if (travel_minutes !== undefined && travel_minutes !== null) {
      // Fallback to explicit travel minutes
      const mins = Number(travel_minutes);
      if (!isNaN(mins) && mins >= 0) {
        travelHours = mins / 60;
      }
    }

    // Prefer geofence (arrival/departure) if present
    if (arrival_time && departure_time) {
      const arrival = toDate(arrival_time);
      const departure = toDate(departure_time);
      const raw = (departure - arrival) / (1000 * 60 * 60);
      if (!isNaN(raw) && raw > 0 && raw < 24) onsiteHours = raw;
    } else if (onsite_minutes !== undefined && onsite_minutes !== null) {
      // Fallback to explicit minutes
      const mins = Number(onsite_minutes);
      if (!isNaN(mins) && mins >= 0) {
        onsiteHours = mins / 60;
      }
    }

    const totalHours = travelHours + onsiteHours;
    return { travelHours, onsiteHours, totalHours };
  } catch (error) {
    console.error('Error calculating hours breakdown:', error);
    return { travelHours: 0, onsiteHours: 0, totalHours: 0 };
  }
};

/**
 * Calculate Coca-Cola revenue based on service type and work status
 */
export const calculateCocaColaRevenue = (serviceType, isRefusal = false, billableHours = 0) => {
  if (serviceType.rate_type === 'HOURLY') {
    // For hourly work, multiply hours by hourly rate
    return billableHours * (serviceType.hourly_rate || 0);
  } else {
    // For flat rate work, use completion or refusal rate
    return isRefusal 
      ? (serviceType.flat_refusal_rate || 0)
      : (serviceType.flat_completion_rate || 0);
  }
};

/**
 * Determine if a location is considered a refusal based on status
 */
export const isLocationRefusal = (status) => {
  if (!status) return false;
  
  const refusalStatuses = [
    'REFUSED',
    'REFUSAL',
    'CANCELLED',
    'CANCELED',
    'CUSTOMER REFUSED',
    'LOCATION REFUSED'
  ];
  
  return refusalStatuses.includes(status.toUpperCase());
};

/**
 * Validate service type data before saving
 */
export const validateServiceType = (serviceType) => {
  const errors = [];

  if (!serviceType.name || serviceType.name.trim().length === 0) {
    errors.push('Service type name is required');
  }

  if (!serviceType.rate_type || !['FLAT', 'HOURLY'].includes(serviceType.rate_type)) {
    errors.push('Rate type must be either FLAT or HOURLY');
  }

  if (serviceType.rate_type === 'FLAT') {
    if (!serviceType.flat_completion_rate || serviceType.flat_completion_rate <= 0) {
      errors.push('Flat completion rate must be greater than 0');
    }
    if (serviceType.flat_refusal_rate === null || serviceType.flat_refusal_rate < 0) {
      errors.push('Flat refusal rate must be 0 or greater');
    }
  }

  if (serviceType.rate_type === 'HOURLY') {
    if (!serviceType.hourly_rate || serviceType.hourly_rate <= 0) {
      errors.push('Hourly rate must be greater than 0');
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Format service type display string
 */
export const formatServiceTypeDisplay = (serviceType) => {
  if (serviceType.rate_type === 'HOURLY') {
    return `${serviceType.name} ($${serviceType.hourly_rate}/hour)`;
  } else {
    return `${serviceType.name} ($${serviceType.flat_completion_rate}/$${serviceType.flat_refusal_rate})`;
  }
};

/**
 * Get service type by code (more reliable than name)
 */
export const getServiceTypeByCode = async (code) => {
  try {
    const { data: serviceTypes, error } = await fetchServiceTypes();
    
    if (error) {
      console.error('Error fetching service types:', error);
      return getDefaultServiceType();
    }

    const serviceType = serviceTypes.find(st => st.code === code);
    return serviceType || getDefaultServiceType();
  } catch (error) {
    console.error('Error getting service type by code:', error);
    return getDefaultServiceType();
  }
};

/**
 * Clear the service types cache
 */
export const clearServiceTypesCache = () => {
  serviceTypesCache = null;
  cacheTimestamp = null;
};

/**
 * Fetch provider service rate configurations for a specific provider
 * @param {string} providerId - The provider's ID
 * @returns {Promise<Object>} Object with data array and error
 */
export const fetchProviderServiceRates = async (providerId) => {
  try {
    const { data, error } = await supabase
      .from('provider_service_rates')
      .select(`
        *,
        service_types (
          id,
          name,
          code,
          flat_completion_rate,
          flat_refusal_rate,
          hourly_rate,
          description,
          is_active
        )
      `)
      .eq('provider_id', providerId);

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('Error fetching provider service rates:', error);
    return { data: [], error };
  }
};

/**
 * Get provider's rate configuration for a specific service type
 * @param {string} providerId - The provider's ID
 * @param {string} serviceTypeId - The service type ID
 * @returns {Promise<Object|null>} The rate configuration or null
 */
export const getProviderServiceRate = async (providerId, serviceTypeId) => {
  try {
    const { data, error } = await supabase
      .from('provider_service_rates')
      .select('*')
      .eq('provider_id', providerId)
      .eq('service_type_id', serviceTypeId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned - this is expected if rate not configured
        return null;
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('Error fetching provider service rate:', error);
    return null;
  }
};

/**
 * Save or update provider service rate configurations
 * @param {string} providerId - The provider's ID
 * @param {Array} serviceRates - Array of service rate configurations
 * @returns {Promise<Object>} Result with success flag
 */
export const saveProviderServiceRates = async (providerId, serviceRates) => {
  try {
    // Prepare records for upsert
    const records = serviceRates.map(rate => {
      const record = {
        provider_id: providerId,
        service_type_id: rate.service_type_id,
        rate_type: rate.rate_type,
        flat_rate: rate.rate_type === 'FLAT' ? rate.flat_rate : null,
        refusal_rate: rate.rate_type === 'FLAT' ? rate.refusal_rate : null,
        hourly_rate: rate.rate_type === 'HOURLY' ? rate.hourly_rate : null,
        updated_at: new Date().toISOString()
      };

      // Add tiered rate fields for Initial Service Call (FLAT rate type)
      if (rate.rate_type === 'FLAT') {
        record.per_board = rate.per_board || null;
        record.per_location = rate.per_location || null;
        record.rate_1_unit = rate.rate_1_unit || null;
        record.rate_2_units = rate.rate_2_units || null;
        record.rate_3_units = rate.rate_3_units || null;
        record.rate_4_units = rate.rate_4_units || null;
        record.rate_5_units = rate.rate_5_units || null;
        record.rate_6_units = rate.rate_6_units || null;
        record.rate_7_units = rate.rate_7_units || null;
        record.rate_8_units = rate.rate_8_units || null;
        record.rate_9_units = rate.rate_9_units || null;
        record.rate_10_units = rate.rate_10_units || null;
      }

      return record;
    });

    const { data, error } = await supabase
      .from('provider_service_rates')
      .upsert(records, {
        onConflict: 'provider_id,service_type_id'
      })
      .select();

    if (error) throw error;

    return { success: true, data, error: null };
  } catch (error) {
    console.error('Error saving provider service rates:', error);
    return { success: false, data: null, error };
  }
};

/**
 * Delete a provider service rate configuration
 * @param {string} providerId - The provider's ID
 * @param {string} serviceTypeId - The service type ID
 * @returns {Promise<Object>} Result with success flag
 */
export const deleteProviderServiceRate = async (providerId, serviceTypeId) => {
  try {
    const { error } = await supabase
      .from('provider_service_rates')
      .delete()
      .eq('provider_id', providerId)
      .eq('service_type_id', serviceTypeId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('Error deleting provider service rate:', error);
    return { success: false, error };
  }
};

/**
 * Validate that a provider has complete rate configurations for all active service types
 * @param {string} providerId - The provider's ID
 * @returns {Promise<Object>} Validation result
 */
export const validateProviderServiceRates = async (providerId) => {
  try {
    // Get all active service types
    const { data: serviceTypes, error: stError } = await fetchServiceTypes();
    if (stError) throw stError;

    // Get provider's configured service rates
    const { data: configuredRates, error: crError } = await fetchProviderServiceRates(providerId);
    if (crError) throw crError;

    const configuredServiceTypeIds = new Set(
      configuredRates.map(r => r.service_type_id)
    );

    const missingConfigurations = serviceTypes.filter(
      st => !configuredServiceTypeIds.has(st.id)
    );

    const isComplete = missingConfigurations.length === 0;

    return {
      isComplete,
      totalServiceTypes: serviceTypes.length,
      configuredServiceTypes: configuredRates.length,
      missingConfigurations: missingConfigurations.map(st => st.name)
    };
  } catch (error) {
    console.error('Error validating provider service rates:', error);
    return {
      isComplete: false,
      error: error.message
    };
  }
}; 